import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Posting } from 'app/library/posting/models/posting.model';
import { IEnvironment } from 'app/projects/core/src/lib/interfaces/environment.interface';
import { MAXBRAIN_ENVIRONMENT } from 'app/projects/core/src/lib/services/environment.token';
import { IEntityApiService } from 'app/projects/entity/src/lib/interfaces/entity.service.interface';
import { EntityService } from 'app/projects/entity/src/lib/services/entity.service';
import { IPaginatedResponse } from 'app/projects/subentity/src/lib/interfaces/paginated-response.interface';
import { IGlobalParticipantQuery } from 'app/projects/user/src/lib/interfaces/participant.query.interface';
import { GlobalParticipant } from 'app/projects/user/src/lib/models/participant.model';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { MaxbrainImage } from '../../image-library/models/maxbrain-image.model';
import { ArticleFilters } from '../interfaces/query/article-filters.interface';
import { IArticleQuery } from '../interfaces/query/article.query.interface';
import { AddArticleCommand } from '../models/add-article.command.model';
import { ArticleImageCommand } from '../models/article-image.command.model';
import { Article } from '../models/article.model';
import { EditArticleCommand } from '../models/edit-article.command.model';

export interface Pagination {
    numberOfPages: number;
    numberOfResults: number;
}

@Injectable()
export class ArticleApiService extends EntityService<IArticleQuery, AddArticleCommand> implements IEntityApiService<Article> {
    articlesPage = 1;

    private _articlesPagination: Pagination = { numberOfPages: 0, numberOfResults: 0 };

    get articlesPagination(): Pagination {
        return this._articlesPagination;
    }
    set articlesPagination(value: Pagination) {
        this._articlesPagination = value;
    }

    constructor(http: HttpClient, @Inject(MAXBRAIN_ENVIRONMENT) environment: IEnvironment) {
        super(http, environment.apiUrl, 'engage/articles');
    }

    create(entity: Article): Observable<Article> {
        const articleData = new AddArticleCommand(entity);

        return this._create(articleData).pipe(map((articleQuery) => new Article(articleQuery)));
    }

    delete(id: string): Observable<any> {
        return this._delete(id);
    }

    read(id: string): Observable<Article> {
        return this.http.get<IArticleQuery>(`${this.apiUrl}/${this.entities}/${id}`).pipe(map((query) => new Article(query)));
    }

    readByUniqueId(uniqueId: string): Observable<Article> {
        return this.http.get<IArticleQuery>(`${this.apiUrl}/${this.entities}/${uniqueId}`).pipe(map((query) => new Article(query)));
    }

    fetchArticleAuthors(): Observable<GlobalParticipant[]> {
        return this.http.get<IGlobalParticipantQuery[]>(`${this.apiUrl}/${this.entities}/authors`).pipe(map((queries) => queries.map((query) => new GlobalParticipant(query))));
    }

    update(entity: Article): Observable<Article> {
        const data = new EditArticleCommand(entity);
        return this.http.patch<IArticleQuery>(`${this.apiUrl}/${this.entities}/${entity.id}`, data).pipe(map((query) => new Article(query)));
    }

    /**
     *
     */
    getAll(): Observable<Article[]> {
        return this.http.get<IArticleQuery[]>(`${this.apiUrl}/engage/articles`).pipe(
            map((queries) =>
                queries.map((query) => {
                    return new Article(query);
                })
            )
        );
    }

    /**
     *
     * @param search
     * @param sortBy
     * @param sortOrder
     * @param sortLanguage
     * @param pageSize
     * @param pageIndex
     */
    fetchArticles(search = '', sortBy = '', sortOrder = '', pageSize = 10, pageIndex = 1, filters?: ArticleFilters): Observable<IPaginatedResponse<IArticleQuery>> {
        let params = new HttpParams().set('page', pageIndex.toString()).set('limit', pageSize.toString());

        if (search) {
            params = params.set('search', search);
        }

        if (sortBy) {
            params = params.set('sortBy', sortBy);
        }

        if (sortOrder) {
            params = params.set('sortOrder', sortOrder);
        }

        if (filters) {
            if (filters.tags && filters.tags.length) {
                params = params.set('filter[tags]', filters.tags.map((tag) => tag.id).toString());
            }

            if (filters.language) {
                params = params.set('filter[language]', filters.language);
            }

            if (filters.author) {
                params = params.set('filter[author]', filters.author.id.toString());
            }
        }

        return this.http.get<IPaginatedResponse<IArticleQuery>>(`${this.apiUrl}/engage/articles`, { params }).pipe(
            map((queries) => {
                this.articlesPagination = {
                    numberOfPages: queries.pagination.number_of_pages,
                    numberOfResults: queries.pagination.number_of_results,
                };
                return queries;
            })
        );
    }

    updateArticleRelatedPostings(articleId: string, postings: Posting[]): Observable<Article> {
        const data = { related_postings: postings.map((posting) => ({ id: posting.id })) };
        return this.http.patch<IArticleQuery>(`${this.apiUrl}/engage/articles/${articleId}`, data).pipe(map((postingQuery) => new Article(postingQuery)));
    }

    /**
     *
     * @param postingId
     */
    removePostingImage(postingId: string): Observable<MaxbrainImage> {
        const data = {
            posting_content: { image: new ArticleImageCommand() },
        };

        return this.http.patch<IArticleQuery>(`${this.apiUrl}/engage/postings/${postingId}`, data).pipe(map((query) => new MaxbrainImage(query.image)));
    }

    /**
     * Method to add Image to entity Posting
     * @param postingId
     * @param image
     */
    addPostingImage(postingId: string, image: MaxbrainImage): Observable<MaxbrainImage> {
        const data = {
            posting_content: { image: new ArticleImageCommand(null, image) },
        };
        return this.http.patch<IArticleQuery>(`${this.apiUrl}/engage/postings/${postingId}`, data).pipe(map((query) => new MaxbrainImage(query.image)));
    }
}
